import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative bg-primary overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Property"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
              <Buildings size={26} weight="fill" />
            </div>
            <div className="leading-tight">
              <span className="font-heading text-xl font-bold block">Property Manager</span>
              <span className="text-xs text-primary-foreground/70 tracking-wide">360 Work Flow</span>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-5xl font-bold leading-none tracking-tight">
              Manage every<br />property, tenant<br />& shilling.
            </h1>
            <p className="mt-6 text-primary-foreground/80 max-w-md leading-relaxed">
              End-to-end property management — sourcing, letting, leases, inspections,
              commissions and landlord accounting. Built for Kenya.
            </p>
          </div>
          <div className="text-primary-foreground/60 text-sm">© {new Date().getFullYear()} Property Manager · 360 Work Flow</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm fade-in">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
              <Buildings size={22} weight="fill" />
            </div>
            <div className="leading-tight">
              <span className="font-heading text-lg font-bold block">Property Manager</span>
              <span className="text-[11px] text-muted-foreground tracking-wide">360 Work Flow</span>
            </div>
          </div>
          <h2 className="font-heading text-3xl font-bold text-[#1A1C18]">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1.5 mb-8">Welcome to Property Manager — 360 Work Flow.</p>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" required data-testid="login-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={show ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
                  data-testid="login-password" />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
